export type EtextWidgetType = "mcq" | "feedback";

export interface EtextWidgetTestConfig {
  widgetType: EtextWidgetType;
  payload: Record<string, unknown>;
}

export interface NormalizedEtextWidgetTestConfig {
  config: EtextWidgetTestConfig;
  /** Dev-shell QE only: widget.js cannot pre-fill via payload; we set the input after mount. */
  feedbackPrefillText?: string;
}

export const DEFAULT_MCQ_PAYLOAD: Record<string, unknown> = {
  question_type: "MCQ",
  questionId: "aacb26a1-32eb-4a15-8ce9-7ddbf7762a78",
  questionStem:
    "Which statement accurately describes how organic compounds act as fuels in catabolic pathways?",
  elementId: "898cd7df-d8f1-48b0-a512-c04bb4d484d1",
  relevantText:
    "Catabolic pathways do not directly move flagella, pump solutes across membranes, polymerize monomers, or perform other cellular work",
  maxAttempts: 3,
  attempts: [],
  options: [
    {
      feedback:
        "Organic compounds must be broken down to release energy; they aren't converted directly into ATP.",
      distractor:
        "They only act as fuels when they are converted directly into ATP without any breakdown.",
      correct_choice: "No",
      id: 1,
    },
    {
      feedback: "Catabolism breaks bonds to release energy, not form new ones.",
      distractor: "They store energy by forming new covalent bonds during catabolism.",
      correct_choice: "No",
      id: 2,
    },
    {
      feedback: "Exactly! Organic compounds release energy in exergonic reactions during catabolism.",
      distractor:
        "They participate in exergonic reactions, releasing potential energy stored in their bonds.",
      correct_choice: "Yes",
      id: 3,
    },
  ],
};

/** Feedback widget (`renderFeedbackWidget`) only reads `assignmentId` from widget.js. */
export const DEFAULT_FEEDBACK_PAYLOAD: Record<string, unknown> = {
  assignmentId: "aacb26a1-32eb-4a15-8ce9-7ddbf7762a78",
  text: "",
};

export const DEFAULT_ETEXT_WIDGET_TEST_CONFIG: EtextWidgetTestConfig = {
  widgetType: "mcq",
  payload: DEFAULT_MCQ_PAYLOAD,
};

export const getDefaultPayloadForType = (widgetType: EtextWidgetType): Record<string, unknown> =>
  widgetType === "feedback" ? DEFAULT_FEEDBACK_PAYLOAD : DEFAULT_MCQ_PAYLOAD;

export const formatPayloadJson = (payload: Record<string, unknown>): string =>
  JSON.stringify(payload, null, 2);

const readNonEmptyString = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

/**
 * Feedback widget payload shape differs from MCQ. widget.js ignores extra fields and
 * does not support pre-fill — only `assignmentId` is passed through; `text` is applied
 * by dev-shell after mount for QE convenience.
 */
export const normalizeEtextWidgetTestConfig = (
  config: EtextWidgetTestConfig,
): NormalizedEtextWidgetTestConfig => {
  if (config.widgetType !== "feedback") {
    return { config };
  }

  const payload = config.payload;
  const assignmentId =
    readNonEmptyString(payload.assignmentId) ??
    readNonEmptyString(payload.questionId) ??
    String(DEFAULT_FEEDBACK_PAYLOAD.assignmentId);

  const feedbackPrefillText =
    readNonEmptyString(payload.text) ?? readNonEmptyString(payload.feedbackResponse);

  return {
    config: {
      widgetType: "feedback",
      payload: { assignmentId },
    },
    feedbackPrefillText,
  };
};

export const parseEtextWidgetPayload = (
  payloadJson: string,
): Record<string, unknown> | null => {
  try {
    const parsed = JSON.parse(payloadJson);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      window.alert("Widget payload must be a JSON object.");
      return null;
    }
    return parsed as Record<string, unknown>;
  } catch {
    window.alert("Invalid JSON in widget payload. Fix the syntax and try again.");
    return null;
  }
};
